import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui';
import { ChevronDownIcon } from './ui';
import { cn } from '../utils/cn';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
];

interface LanguageSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'inline';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className, 
  variant = 'dropdown' 
}) => {
  const { i18n, t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (languageCode: string) => {
    // Update i18n language
    i18n.changeLanguage(languageCode);
    
    // Update URL with new language prefix
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isLanguageInPath = languages.some(lang => lang.code === pathSegments[0]);
    
    let newPath = '';
    if (isLanguageInPath) {
      // Replace existing language prefix
      pathSegments[0] = languageCode;
      newPath = '/' + pathSegments.join('/');
    } else {
      // Add language prefix
      newPath = `/${languageCode}${location.pathname}`;
    }
    
    // Preserve search params
    const searchParams = location.search;
    navigate(newPath + searchParams, { replace: true });
    
    setIsOpen(false);
  };

  if (variant === 'inline') {
    return (
      <div className={cn('flex gap-2', className)}>
        {languages.map((language) => (
          <Button
            key={language.code}
            variant={language.code === i18n.language ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => changeLanguage(language.code)}
            className="min-w-0 px-3"
          >
            <span className="mr-1">{language.flag}</span>
            {language.code.toUpperCase()}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        rightIcon={<ChevronDownIcon size="sm" />}
        className="min-w-0 rounded-full border border-neutral-50/35 bg-neutral-50/15 px-3 text-neutral-50 hover:bg-neutral-50/25 hover:text-neutral-50 dark:border-neutral-50/35 dark:bg-neutral-50/15 dark:text-neutral-50 dark:hover:bg-neutral-50/25"
        aria-label={t('selectLanguage')}
      >
        <span className="mr-2">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <span className="sm:hidden">{currentLanguage.code.toUpperCase()}</span>
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              className="absolute right-0 top-full mt-2 z-50 min-w-[160px] bg-neutral-50/90 dark:bg-neutral-800/90 backdrop-blur-sm rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.15 }}
            >
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  className={cn(
                    'w-full px-4 py-3 text-left flex items-center gap-3 transition-colors',
                    'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                    language.code === i18n.language && 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-300'
                  )}
                >
                  <span className="text-lg">{language.flag}</span>
                  <span className="font-medium">{language.name}</span>
                  {language.code === i18n.language && (
                    <span className="ml-auto text-primary-500">✓</span>
                  )}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
